import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../../Support/with_presenter';
import {SaveSnippetPresenter} from './SaveSnippetPresenter';
import {FullWorkout} from '../../../../Models/FullWorkout';
import {useNavigate} from 'react-router-dom';
import {Report} from '../../../../Models/Report';
import {ObservableNetworkRequestStatusView} from '../../../Components/Utils/ObservableNetworkRequestStatusView';
import {Stroke} from '../../../../Models/Stroke';
import {RangeInput} from '../../../../Models/RangeInput';
import {calculateRangeOverview} from '../../../../Strokes/calculateRangeOverview';
import {Me} from '../../../../Models/Me';
import {Duration} from '../../../../Domain/Duration';
import { Distance } from '../../../../Domain/Distance';
import { calculateIndoorWorkoutListSummary } from '../../../../Strokes/calculateIndoorWorkoutListSummary';

interface OwnProps {
    fullWorkout: FullWorkout;
    className?: string;
    strokes: Stroke[];
}

const SaveSnippetComponent: FunctionComponent<OwnProps & PresenterProps<SaveSnippetPresenter>> = observer(
    ({presenter, fullWorkout, strokes}) => {
        const navigate = useNavigate();
        const workoutSummary = calculateIndoorWorkoutListSummary(fullWorkout);
        const input: RangeInput = {
            start: Duration.zero(),
            end: workoutSummary.duration,
            predetermined: {
                totalDistance: workoutSummary.distance,
                totalDuration: workoutSummary.duration,
            },
        };

        const overview = calculateRangeOverview(input, strokes);


        return (
            <div className="card mb-3 mt-lg-0 mt-3">
                <div className="card-title">
                    <h5 className="d-flex justify-content-between align-items-baseline flex-row flex-wrap">
                        <span>Add to report</span>
                    </h5>
                </div>
                <div className="card-body d-flex flex-column">
                    <div className="row">
                        <div className="col-12">
                            <p>Select a range or add your entire workout to a report for analysis.</p>
                            <div className="mb-3">
                                <ObservableNetworkRequestStatusView
                                    result={presenter.reports}
                                    loader={() => <span>Loading reports</span>}
                                    error={() => <span>Error loading reports</span>}
                                    component={(data) => (
                                        <>
                                            <label htmlFor="report" className="mb-2 form-label">
                                                Select a report to add to
                                            </label>
                                            <select
                                                className="form-select"
                                                value={presenter.selectedReport?.id ?? ''}
                                                name="report"
                                                onChange={(e) => {
                                                    presenter.setSelectedReport(
                                                        data.find((report) => {
                                                            // parseInt since mobx proxied objects are not equal
                                                            // @ts-ignore
                                                            // tslint:disable-next-line:triple-equals
                                                            return (
                                                                parseInt(report.id, 10) === parseInt(e.target.value, 10)
                                                            );
                                                        })!,
                                                    );
                                                }}
                                            >
                                                <option value="">Select a report</option>
                                                {data.map((report) => (
                                                    <option key={report.id} value={report.id}>
                                                        {report.name} - {report.type}
                                                    </option>
                                                ))}
                                            </select>
                                        </>
                                    )}
                                />
                            </div>
                            <div className="mb-3">
                                <div className="d-flex flex-row justify-content-end">
                                    <button
                                        className="btn btn-primary"
                                        disabled={presenter.disabledForm}
                                        onClick={async () => {
                                            const result = await presenter.onSubmit(input, overview);
                                            if (result.status === 'success') {
                                                navigate('/reports/' + presenter.selectedReport?.id);
                                            }
                                        }}
                                    >
                                        Save & view
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export const SaveSnippet = withPresenter<SaveSnippetPresenter, OwnProps>(
    (props, component) =>
        new SaveSnippetPresenter(
            props.fullWorkout,
            component.network.snippets,
            component.network.report,
            component.localPreferences,
        ),
    SaveSnippetComponent,
);
